import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Clock, Bot, Globe, Code2, Clapperboard, ChevronDown } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Container, PageHeader, Section, SectionHeading, CtaBand, Arrow, buttonClass } from "../components/site";
import { Reveal } from "../components/motion";
import { useI18n } from "../i18n";

// `title` and plan names are the English values Checkout.jsx expects in its form state,
// so "Choose" can preselect them. Everything shown on screen comes from the translations.
const services = [
  {
    id: "ai-automation",
    icon: Bot,
    title: "AI Automation Systems",
    plans: ["Starter Automation", "Business Automation Suite", "Enterprise AI Systems"],
    stack: ["OpenAI", "LangChain", "Pinecone", "ChromaDB", "n8n", "FastAPI"],
  },
  {
    id: "web-development",
    icon: Globe,
    title: "Custom Website Development",
    plans: ["Static Business Website", "Dynamic Website with Admin", "Full Custom Web App"],
    stack: ["React", "Next.js", "Tailwind CSS", "Node.js", "FastAPI", "MongoDB", "PostgreSQL", "Azure"],
  },
  {
    id: "python-automation",
    icon: Code2,
    title: "Python Automation & Data Solutions",
    plans: ["Simple Automation Script", "Web Scraping Solution", "Complex Data Pipeline"],
    stack: ["Python", "Pandas", "Scrapy", "Selenium", "BeautifulSoup", "Celery", "Airflow"],
  },
  {
    id: "ai-video",
    icon: Clapperboard,
    title: "AI Video & Avatar Systems",
    plans: ["Basic AI Video Setup", "Professional Avatar System", "Enterprise Video Platform"],
    stack: ["ComfyUI", "Stable Diffusion", "Voice synthesis", "FFmpeg", "Node.js", "FastAPI"],
  },
];

const ServiceBlock = ({ service, index }) => {
  const { t } = useI18n();
  const [showTools, setShowTools] = useState(false);
  const base = `services.items.${service.id}`;
  const includes = t(`${base}.includes`);
  const plans = t(`${base}.plans`);
  const Icon = service.icon;

  return (
    <section id={service.id} className="scroll-mt-24">
      <Reveal className="overflow-hidden rounded-[2rem] border border-line bg-surface">
        <div className="grid gap-10 p-7 md:p-10 lg:grid-cols-12 lg:gap-12">
          {/* Summary */}
          <div className="lg:col-span-6">
            <div className="flex items-center gap-4">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-soft text-brand">
                <Icon size={24} />
              </span>
              <span className="font-mono text-xs text-ink-3">
                0{index + 1} / 0{services.length}
              </span>
            </div>
            <h2 className="mt-8 font-display text-4xl leading-[1.08] text-ink md:text-5xl">{t(`${base}.title`)}</h2>
            <p className="mt-4 max-w-md text-lg leading-relaxed text-ink-2">{t(`${base}.tagline`)}</p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 text-sm text-ink-2">
              <Clock size={14} className="text-brand" />
              {t("services.timeline")}: <span className="font-medium text-ink">{t(`${base}.timeline`)}</span>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/checkout" state={{ service: service.title }} className={`group ${buttonClass("primary")}`}>
                {t("services.discuss")}
                <Arrow />
              </Link>
              <button
                type="button"
                onClick={() => setShowTools((v) => !v)}
                aria-expanded={showTools}
                className={buttonClass("secondary")}
              >
                {showTools ? t("services.hideTools") : t("services.showTools")}
                <ChevronDown size={16} className={`transition-transform duration-300 ${showTools ? "rotate-180" : ""}`} />
              </button>
            </div>
            <div
              className={`grid transition-all duration-500 ease-out ${
                showTools ? "mt-6 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              }`}
            >
              <ul className="flex flex-wrap gap-2 overflow-hidden" aria-label={t("services.tools")}>
                {service.stack.map((tool) => (
                  <li key={tool} className="rounded-full bg-paper px-3 py-1.5 font-mono text-xs text-ink-2">
                    {tool}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Included */}
          <div className="lg:col-span-6">
            <h3 className="text-sm font-semibold text-ink">{t("services.included")}</h3>
            <ul className="mt-4 space-y-2">
              {includes.map((item, i) => (
                <Reveal
                  as="li"
                  key={item}
                  delay={i * 60}
                  className="flex items-center gap-3 rounded-2xl border border-line bg-paper px-4 py-3.5 text-[0.9375rem] text-ink"
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand text-on-brand">
                    <Check size={13} strokeWidth={3} />
                  </span>
                  {item}
                </Reveal>
              ))}
            </ul>
          </div>
        </div>

        {/* Ways to start */}
        <div className="border-t border-line bg-paper/60 p-7 md:p-10">
          <h3 className="text-sm font-semibold text-ink">{t("services.options")}</h3>
          <div className="mt-4 grid gap-3 lg:grid-cols-3">
            {service.plans.map((planName, i) => (
              <Link
                key={planName}
                to="/checkout"
                state={{ service: service.title, plan: planName }}
                className="card-lift group flex items-center justify-between gap-4 rounded-2xl border border-line bg-surface p-5 hover:border-brand/50"
              >
                <span>
                  <span className="block font-medium text-ink">{plans[i]?.name}</span>
                  <span className="mt-1 block text-sm text-ink-3">{plans[i]?.note}</span>
                </span>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-line text-ink transition-all duration-300 group-hover:border-brand group-hover:bg-brand group-hover:text-on-brand">
                  <Arrow size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
};

const Services = () => {
  const { t } = useI18n();
  const extras = t("services.extras");

  return (
    <>
      <Header />

      <PageHeader eyebrow={t("services.eyebrow")} title={t("services.title")} lede={t("services.lede")}>
        <nav className="mt-10 flex flex-wrap gap-2" aria-label={t("services.eyebrow")}>
          {services.map((s) => (
            <a key={s.id} href={`#${s.id}`} className={buttonClass("secondary", "sm")}>
              {t(`services.items.${s.id}.title`)}
            </a>
          ))}
        </nav>
      </PageHeader>

      <Section>
        <div className="space-y-6 md:space-y-8">
          {services.map((service, index) => (
            <ServiceBlock key={service.id} service={service} index={index} />
          ))}
        </div>
      </Section>

      <Section className="pt-0 md:pt-0">
        <SectionHeading title={t("services.extrasTitle")} center />
        <div className="flex flex-wrap justify-center gap-3">
          {extras.map((item, i) => (
            <Reveal
              key={item}
              delay={i * 60}
              variant="scale"
              className="rounded-full border border-line bg-surface px-5 py-3 text-[0.9375rem] text-ink transition-colors hover:border-brand hover:text-brand"
            >
              {item}
            </Reveal>
          ))}
        </div>
      </Section>

      <CtaBand secondary={{ to: "/about", label: t("common.nav.about") }} />
      <Footer />
    </>
  );
};

export default Services;
